import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import withRoleGuard from '../hoc/withRoleGuard';
import { motion } from 'framer-motion';
import {
  FaUsers,
  FaUserGraduate,
  FaVenusMars,
  FaChartBar,
  FaChartArea,
  FaChartPie,
  FaCalendarAlt,
  FaCalendarDay,
  FaCalendarWeek,
  FaClock,
  FaHeartbeat,
  FaClipboardCheck,
  FaUserCheck,
  FaSyncAlt,
  FaBirthdayCake,
  FaStethoscope,
} from 'react-icons/fa';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  Filler,
} from 'chart.js';
import AssistantSidebar from './AssistantSidebar';
import Navbar from '../student/Navbar';
import Loader from '../components/Loader';
import cache, { CacheKeys, CacheTTL } from '../utils/cache';
import { formatDateEG } from '../utils/dateUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  Filler
);

const API = process.env.REACT_APP_API_URL;

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const item = {
  hidden: { y: 16, opacity: 0 },
  show: { y: 0, opacity: 1 },
};

// Brand palette - kept in step with the sidebar / navbar colours.
const BRAND = {
  primary: '#0077B6',
  light: '#00A6ED',
  teal: '#20B2AA',
  amber: '#F7931E',
  orange: '#FF6B35',
  purple: '#7048E8',
  green: '#28A745',
  pink: '#E86BA0',
  slate: '#64748B',
};

const SERIES = [
  BRAND.primary, BRAND.light, BRAND.teal, BRAND.amber,
  BRAND.orange, BRAND.purple, BRAND.green, BRAND.pink,
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const FONT = "'Inter', sans-serif";

/* ------------------------------------------------------------------ */
/* Scope helpers                                                       */
/* ------------------------------------------------------------------ */

// A scope is {scope: 'all' | 'year' | 'month', year, month}. Normalising it
// keeps cache keys stable so switching back and forth never refetches.
const normalizeScope = (s) => ({
  scope: s.scope,
  year: s.scope === 'all' ? 0 : s.year,
  month: s.scope === 'month' ? s.month : 0,
});

const scopeKey = (s) => {
  const n = normalizeScope(s);
  return `${n.scope}_${n.year}_${n.month}`;
};

// Keep a decimal when a real but tiny share would otherwise render as "0%".
const formatShare = (part, total) => {
  if (!total) return '0%';
  const pct = (part / total) * 100;
  if (pct > 0 && pct < 1) return `${pct.toFixed(1)}%`;
  return `${Math.round(pct)}%`;
};

const scopeLabel = (s) => {
  if (s.scope === 'all') return 'All time';
  if (s.scope === 'year') return String(s.year);
  return `${MONTH_NAMES[s.month]} ${s.year}`;
};

/* ------------------------------------------------------------------ */
/* Shared chart options                                                */
/* ------------------------------------------------------------------ */

const tooltipStyle = {
  backgroundColor: 'rgba(17, 24, 39, 0.92)',
  titleColor: '#ffffff',
  bodyColor: '#E5E7EB',
  borderColor: 'rgba(255,255,255,0.12)',
  borderWidth: 1,
  padding: 12,
  cornerRadius: 8,
  displayColors: true,
  bodyFont: { family: FONT },
  titleFont: { family: FONT, weight: 'bold' },
};

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: tooltipStyle },
};

const axisOptions = ({ stacked = false, xTicks = {}, ...rest } = {}) => ({
  ...baseOptions,
  ...rest,
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { family: FONT, size: 11 }, color: '#6B7280', maxRotation: 0, ...xTicks },
      stacked,
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.06)', drawBorder: false },
      ticks: { precision: 0, font: { family: FONT, size: 11 }, color: '#6B7280' },
      stacked,
    },
  },
});

const horizontalBarOptions = {
  ...baseOptions,
  indexAxis: 'y',
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: 'rgba(0,0,0,0.06)', drawBorder: false },
      ticks: { precision: 0, font: { family: FONT, size: 11 }, color: '#6B7280' },
    },
    y: {
      grid: { display: false },
      ticks: { font: { family: FONT, size: 11 }, color: '#374151' },
    },
  },
};

const doughnutOptions = {
  ...baseOptions,
  cutout: '62%',
  plugins: {
    tooltip: {
      ...tooltipStyle,
      callbacks: {
        label: (ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const pct = total ? Math.round((ctx.raw / total) * 100) : 0;
          return ` ${ctx.label}: ${ctx.raw} (${pct}%)`;
        },
      },
    },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        font: { family: FONT, size: 12 },
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 16,
        color: '#374151',
      },
    },
  },
};

const stackedLegend = {
  display: true,
  position: 'bottom',
  labels: {
    font: { family: FONT, size: 12 },
    usePointStyle: true,
    pointStyle: 'circle',
    padding: 16,
    color: '#374151',
  },
};

/* ------------------------------------------------------------------ */
/* Small presentational pieces                                         */
/* ------------------------------------------------------------------ */

const ScopeFilter = ({ value, onChange, years, compact = false }) => {
  const scopes = [
    { id: 'all', label: 'All time' },
    { id: 'year', label: 'Yearly' },
    { id: 'month', label: 'Monthly' },
  ];

  const now = new Date();
  const yearOptions = years && years.length ? years : [now.getFullYear()];

  const setScope = (scope) => {
    onChange({
      scope,
      year: value.year || now.getFullYear(),
      month: value.month != null ? value.month : now.getMonth(),
    });
  };

  const selectClass =
    'px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-lg bg-white text-gray-700 ' +
    'focus:outline-none focus:ring-2 focus:ring-[#0077B6]/25 focus:border-[#0077B6] cursor-pointer';

  return (
    <div className={`flex flex-wrap items-center gap-2 ${compact ? '' : 'gap-3'}`}>
      <div className="inline-flex rounded-lg bg-gray-100 p-0.5" role="group" aria-label="Time range">
        {scopes.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setScope(s.id)}
            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
              value.scope === s.id
                ? 'bg-white text-[#0077B6] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {value.scope === 'month' && (
        <select
          value={value.month}
          onChange={(e) => onChange({ ...value, month: parseInt(e.target.value, 10) })}
          className={selectClass}
          aria-label="Month"
        >
          {MONTH_NAMES.map((name, idx) => (
            <option key={idx} value={idx}>{name}</option>
          ))}
        </select>
      )}

      {value.scope !== 'all' && (
        <select
          value={value.year}
          onChange={(e) => onChange({ ...value, year: parseInt(e.target.value, 10) })}
          className={selectClass}
          aria-label="Year"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      )}
    </div>
  );
};

const ChartCard = ({ icon: Icon, title, subtitle, filter, height = 'h-72', children, span }) => (
  <motion.div
    variants={item}
    className={`bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col ${span || ''}`}
  >
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 pt-5 pb-3">
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          {Icon && (
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#0077B6]/10 text-[#0077B6] shrink-0">
              <Icon className="h-4 w-4" />
            </span>
          )}
          <span className="truncate">{title}</span>
        </h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1 ml-10">{subtitle}</p>}
      </div>
      {filter}
    </div>
    <div className={`px-5 pb-5 ${height}`}>{children}</div>
  </motion.div>
);

const EmptyChart = ({ message = 'No data for this period' }) => (
  <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
    <FaChartBar className="h-7 w-7 opacity-40" />
    <span className="text-sm">{message}</span>
  </div>
);

const ChartLoading = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-[#0077B6]" />
  </div>
);

const StatCard = ({ icon: Icon, label, value, hint, accent = false }) => (
  <motion.div
    variants={item}
    className={
      accent
        ? 'rounded-2xl p-4 bg-gradient-to-br from-[#0077B6] to-[#00A6ED] shadow-md text-white'
        : 'rounded-2xl p-4 bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow'
    }
  >
    <div className="flex items-center gap-2 mb-1.5">
      {Icon && (
        <Icon className={`h-4 w-4 ${accent ? 'text-white/80' : 'text-[#0077B6]'}`} />
      )}
      <p className={`text-xs font-medium ${accent ? 'text-white/90' : 'text-gray-500'}`}>{label}</p>
    </div>
    <p className={`text-2xl font-bold ${accent ? 'text-white' : 'text-gray-800'}`}>{value}</p>
    {hint && <p className={`text-[11px] mt-1 ${accent ? 'text-white/70' : 'text-gray-400'}`}>{hint}</p>}
  </motion.div>
);

const SectionHeader = ({ icon: Icon, title, description, right }) => (
  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
    <div className="flex items-center gap-3">
      <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0077B6] text-white shadow-sm">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h2 className="text-lg font-bold text-gray-800">{title}</h2>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
    </div>
    {right}
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const Analytics = () => {
  const now = useMemo(() => new Date(), []);

  const defaultScope = useMemo(
    () => ({ scope: 'all', year: now.getFullYear(), month: now.getMonth() }),
    [now]
  );

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // One global filter, plus per-chart overrides so a single chart (gender, age,
  // students, ...) can be inspected monthly while the rest stays on all time.
  const [globalScope, setGlobalScope] = useState(defaultScope);
  const [overrides, setOverrides] = useState({});

  // scopeKey -> analytics payload
  const [analyticsByScope, setAnalyticsByScope] = useState({});
  const [pendingScopes, setPendingScopes] = useState({});

  const [apptScope, setApptScope] = useState({ scope: 'year', year: now.getFullYear(), month: now.getMonth() });
  const [apptAnalytics, setApptAnalytics] = useState(null);
  const [apptLoading, setApptLoading] = useState(true);

  const [entriesMonth, setEntriesMonth] = useState(now.getMonth());
  const [entriesYear, setEntriesYear] = useState(now.getFullYear());
  const [entriesData, setEntriesData] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(true);

  const authConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  /* ---------------- data loading ---------------- */

  const fetchAnalyticsForScope = useCallback(async (scopeValue) => {
    const key = scopeKey(scopeValue);
    const n = normalizeScope(scopeValue);

    setPendingScopes((p) => ({ ...p, [key]: true }));
    try {
      const data = await cache.getOrSet(
        CacheKeys.ASSISTANT_ANALYTICS(n.scope, n.year, n.month),
        async () => {
          const res = await axios.get(
            `${API}/api/assistant/analytics?scope=${n.scope}&year=${n.year || now.getFullYear()}&month=${n.month}`,
            authConfig()
          );
          return res.data;
        },
        CacheTTL.LONG
      );
      setAnalyticsByScope((prev) => ({ ...prev, [key]: data }));
      return data;
    } catch (err) {
      setError('Failed to load analytics data');
      return null;
    } finally {
      setPendingScopes((p) => {
        const next = { ...p };
        delete next[key];
        return next;
      });
    }
  }, [authConfig, now]);

  const fetchApptAnalytics = useCallback(async (scopeValue) => {
    const n = normalizeScope(scopeValue);
    setApptLoading(true);
    try {
      const data = await cache.getOrSet(
        CacheKeys.ASSISTANT_APPOINTMENT_ANALYTICS(n.scope, n.year, n.month),
        async () => {
          const res = await axios.get(
            `${API}/api/assistant/appointment-analytics?scope=${n.scope}&year=${n.year || now.getFullYear()}&month=${n.month}`,
            authConfig()
          );
          return res.data;
        },
        CacheTTL.MEDIUM
      );
      setApptAnalytics(data);
    } catch (err) {
      // Appointment analytics is supplementary - never block the page for it.
      setApptAnalytics(null);
    } finally {
      setApptLoading(false);
    }
  }, [authConfig, now]);

  const fetchEntries = useCallback(async (month, year) => {
    setEntriesLoading(true);
    try {
      const data = await cache.getOrSet(
        CacheKeys.PATIENT_ENTRIES(year, month),
        async () => {
          const res = await axios.get(
            `${API}/api/assistant/patient-entries?month=${month}&year=${year}`,
            authConfig()
          );
          return res.data;
        },
        CacheTTL.MEDIUM
      );
      setEntriesData(data);
    } catch (err) {
      console.error('Failed to fetch patient entries:', err);
    } finally {
      setEntriesLoading(false);
    }
  }, [authConfig]);

  // Initial load
  useEffect(() => {
    let active = true;
    (async () => {
      await fetchAnalyticsForScope(defaultScope);
      if (active) setLoading(false);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchEntries(entriesMonth, entriesYear); }, [entriesMonth, entriesYear, fetchEntries]);
  useEffect(() => { fetchApptAnalytics(apptScope); }, [apptScope, fetchApptAnalytics]);

  // Fetch any scope that a chart asks for and we do not have yet.
  useEffect(() => {
    const all = [globalScope, ...Object.values(overrides).filter(Boolean)];
    all.forEach((s) => {
      const key = scopeKey(s);
      if (!analyticsByScope[key] && !pendingScopes[key]) {
        fetchAnalyticsForScope(s);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalScope, overrides]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    cache.invalidatePrefix('analytics_');
    cache.invalidatePrefix('patient_entries_');
    setAnalyticsByScope({});
    await Promise.all([
      fetchAnalyticsForScope(globalScope),
      fetchApptAnalytics(apptScope),
      fetchEntries(entriesMonth, entriesYear),
    ]);
    setRefreshing(false);
  }, [fetchAnalyticsForScope, fetchApptAnalytics, fetchEntries, globalScope, apptScope, entriesMonth, entriesYear]);

  /* ---------------- per-chart accessors ---------------- */

  const globalData = analyticsByScope[scopeKey(globalScope)] || null;

  const availableYears = useMemo(
    () => (globalData?.availableYears?.length ? globalData.availableYears : [now.getFullYear()]),
    [globalData, now]
  );

  // Resolve the scope a given chart is using (its override, else the global one).
  const scopeFor = useCallback(
    (chartId) => overrides[chartId] || globalScope,
    [overrides, globalScope]
  );

  const dataFor = useCallback(
    (chartId) => analyticsByScope[scopeKey(scopeFor(chartId))] || null,
    [analyticsByScope, scopeFor]
  );

  const isLoadingFor = useCallback(
    (chartId) => Boolean(pendingScopes[scopeKey(scopeFor(chartId))]) && !dataFor(chartId),
    [pendingScopes, scopeFor, dataFor]
  );

  const filterFor = useCallback((chartId) => (
    <ScopeFilter
      value={scopeFor(chartId)}
      years={availableYears}
      onChange={(next) => setOverrides((prev) => ({ ...prev, [chartId]: next }))}
      compact
    />
  ), [scopeFor, availableYears]);

  /* ---------------- chart datasets ---------------- */

  const genderData = dataFor('gender');
  const genderChart = useMemo(() => {
    if (!genderData) return null;
    const values = [genderData.male || 0, genderData.female || 0, genderData.otherGender || 0];
    if (values.every((v) => v === 0)) return null;
    return {
      labels: ['Male', 'Female', 'Other / Not set'],
      datasets: [{
        data: values,
        backgroundColor: [BRAND.primary, BRAND.pink, BRAND.slate],
        borderColor: '#fff',
        borderWidth: 3,
        hoverOffset: 6,
      }],
    };
  }, [genderData]);

  const ageData = dataFor('age');
  const ageChart = useMemo(() => {
    if (!ageData?.ageGroups) return null;
    const labels = Object.keys(ageData.ageGroups);
    const values = Object.values(ageData.ageGroups);
    if (!labels.length || values.every((v) => v === 0)) return null;
    return {
      labels,
      datasets: [{
        label: 'Patients',
        data: values,
        backgroundColor: labels.map((_, i) => SERIES[i % SERIES.length]),
        borderRadius: 6,
        maxBarThickness: 52,
      }],
    };
  }, [ageData]);

  const ageGenderData = dataFor('ageGender');
  const ageGenderChart = useMemo(() => {
    if (!ageGenderData?.ageByGender) return null;
    const labels = Object.keys(ageGenderData.ageByGender.male || {});
    if (!labels.length) return null;
    const male = labels.map((l) => ageGenderData.ageByGender.male[l] || 0);
    const female = labels.map((l) => ageGenderData.ageByGender.female[l] || 0);
    if ([...male, ...female].every((v) => v === 0)) return null;
    return {
      labels,
      datasets: [
        { label: 'Male', data: male, backgroundColor: BRAND.primary, borderRadius: 5, maxBarThickness: 34 },
        { label: 'Female', data: female, backgroundColor: BRAND.pink, borderRadius: 5, maxBarThickness: 34 },
      ],
    };
  }, [ageGenderData]);

  const assignmentData = dataFor('assignment');
  const assignmentChart = useMemo(() => {
    if (!assignmentData) return null;
    const values = [assignmentData.assigned || 0, assignmentData.unassigned || 0];
    if (values.every((v) => v === 0)) return null;
    return {
      labels: ['Assigned to a student', 'Unassigned'],
      datasets: [{
        data: values,
        backgroundColor: [BRAND.green, BRAND.orange],
        borderColor: '#fff',
        borderWidth: 3,
        hoverOffset: 6,
      }],
    };
  }, [assignmentData]);

  const trendData = dataFor('trend');
  const trendChart = useMemo(() => {
    if (!trendData?.trend?.labels?.length) return null;
    if (trendData.trend.counts.every((v) => v === 0)) return null;
    return {
      labels: trendData.trend.labels,
      datasets: [{
        label: 'New patients',
        data: trendData.trend.counts,
        borderColor: BRAND.primary,
        backgroundColor: 'rgba(0, 119, 182, 0.12)',
        borderWidth: 3,
        tension: 0.35,
        fill: true,
        pointBackgroundColor: BRAND.primary,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: trendData.trend.labels.length > 20 ? 0 : 4,
        pointHoverRadius: 6,
      }],
    };
  }, [trendData]);

  const trendAxisLabel = {
    day: 'Day of month',
    month: 'Month',
    year: 'Year',
  }[trendData?.trend?.granularity] || '';

  const studentsData = dataFor('students');
  const studentsChart = useMemo(() => {
    const top = studentsData?.topStudents || [];
    if (!top.length) return null;
    return {
      labels: top.map((s) => (s.name?.length > 24 ? `${s.name.slice(0, 22)}...` : s.name)),
      datasets: [{
        label: 'Patients',
        data: top.map((s) => s.count),
        backgroundColor: top.map((_, i) => SERIES[i % SERIES.length]),
        borderRadius: 5,
        maxBarThickness: 20,
      }],
    };
  }, [studentsData]);

  const weekdayData = dataFor('weekday');
  const weekdayChart = useMemo(() => {
    if (!weekdayData?.weekday?.counts) return null;
    if (weekdayData.weekday.counts.every((v) => v === 0)) return null;
    return {
      labels: weekdayData.weekday.labels,
      datasets: [{
        label: 'Registrations',
        data: weekdayData.weekday.counts,
        backgroundColor: weekdayData.weekday.counts.map((_, i) =>
          i === 0 || i === 6 ? 'rgba(0, 166, 237, 0.65)' : 'rgba(0, 119, 182, 0.8)'
        ),
        borderRadius: 6,
        maxBarThickness: 44,
      }],
    };
  }, [weekdayData]);

  const conditionsData = dataFor('conditions');
  const conditionsChart = useMemo(() => {
    const top = conditionsData?.topConditions || [];
    if (!top.length) return null;
    return {
      labels: top.map((c) => c.name),
      datasets: [{
        label: 'Patients',
        data: top.map((c) => c.count),
        backgroundColor: top.map((_, i) => SERIES[i % SERIES.length]),
        borderRadius: 5,
        maxBarThickness: 20,
      }],
    };
  }, [conditionsData]);

  const completenessData = dataFor('completeness');
  const completenessChart = useMemo(() => {
    const c = completenessData?.completeness;
    const total = completenessData?.total || 0;
    if (!c || !total) return null;
    return {
      labels: ['Phone number', 'Chief complaint', 'Chronic history'],
      datasets: [
        {
          label: 'Recorded',
          data: [c.withPhone, c.withChiefComplaint, c.withChronicDisease],
          backgroundColor: BRAND.teal,
          borderRadius: 5,
          maxBarThickness: 34,
        },
        {
          label: 'Missing',
          data: [
            total - c.withPhone,
            total - c.withChiefComplaint,
            total - c.withChronicDisease,
          ],
          backgroundColor: '#E2E8F0',
          borderRadius: 5,
          maxBarThickness: 34,
        },
      ],
    };
  }, [completenessData]);

  /* ---------------- appointments ---------------- */

  const apptStatusChart = useMemo(() => {
    const counts = apptAnalytics?.statusCounts;
    if (!counts || !Object.keys(counts).length) return null;
    const labels = Object.keys(counts);
    const colorByStatus = {
      completed: BRAND.green,
      pending: BRAND.amber,
      cancelled: BRAND.orange,
      unknown: BRAND.slate,
    };
    return {
      labels: labels.map((l) => l.charAt(0).toUpperCase() + l.slice(1)),
      datasets: [{
        data: labels.map((l) => counts[l]),
        backgroundColor: labels.map((l, i) => colorByStatus[l] || SERIES[i % SERIES.length]),
        borderColor: '#fff',
        borderWidth: 3,
        hoverOffset: 6,
      }],
    };
  }, [apptAnalytics]);

  const apptHourChart = useMemo(() => {
    const hourly = apptAnalytics?.hourly;
    if (!hourly?.counts?.length || hourly.counts.every((v) => v === 0)) return null;
    // Trim to the hours the clinic actually operates.
    const first = hourly.counts.findIndex((v) => v > 0);
    const last = hourly.counts.length - 1 - [...hourly.counts].reverse().findIndex((v) => v > 0);
    const from = Math.max(0, first - 1);
    const to = Math.min(hourly.counts.length - 1, last + 1);
    return {
      labels: hourly.labels.slice(from, to + 1),
      datasets: [{
        label: 'Appointments',
        data: hourly.counts.slice(from, to + 1),
        backgroundColor: 'rgba(32, 178, 170, 0.8)',
        borderRadius: 6,
        maxBarThickness: 34,
      }],
    };
  }, [apptAnalytics]);

  const apptTypeChart = useMemo(() => {
    const types = apptAnalytics?.typeCounts;
    if (!types || !Object.keys(types).length) return null;
    const labels = Object.keys(types);
    return {
      labels: labels.map((l) => (l.length > 24 ? `${l.slice(0, 22)}...` : l)),
      datasets: [{
        label: 'Appointments',
        data: labels.map((l) => types[l]),
        backgroundColor: labels.map((_, i) => SERIES[i % SERIES.length]),
        borderRadius: 5,
        maxBarThickness: 20,
      }],
    };
  }, [apptAnalytics]);

  /* ---------------- entries (day / week) ---------------- */

  const monthlyDailyChart = useMemo(() => {
    if (!entriesData?.monthly) return null;
    return {
      labels: entriesData.monthly.labels,
      datasets: [{
        label: 'Patients entered',
        data: entriesData.monthly.counts,
        backgroundColor: 'rgba(0, 119, 182, 0.75)',
        hoverBackgroundColor: BRAND.primary,
        borderRadius: 4,
        maxBarThickness: 18,
      }],
    };
  }, [entriesData]);

  const monthlyDailyOptions = useMemo(() => axisOptions({
    plugins: {
      legend: { display: false },
      tooltip: {
        ...tooltipStyle,
        callbacks: {
          title: (items) => `${items[0].label} ${MONTH_NAMES[entriesMonth]} ${entriesYear}`,
          label: (ctx) => ` ${ctx.raw} patient${ctx.raw !== 1 ? 's' : ''} entered`,
        },
      },
    },
    xTicks: {
      callback: function (val, index) {
        const label = this.getLabelForValue(index);
        return parseInt(label, 10) % 2 === 1 ? label : '';
      },
    },
  }), [entriesMonth, entriesYear]);

  const weeklyChart = useMemo(() => {
    if (!entriesData?.weekly) return null;
    return {
      labels: entriesData.weekly.labels,
      datasets: [{
        label: 'Patients entered',
        data: entriesData.weekly.counts,
        backgroundColor: entriesData.weekly.counts.map((_, i) =>
          i === 0 || i === 6 ? 'rgba(0, 166, 237, 0.7)' : 'rgba(0, 119, 182, 0.85)'
        ),
        borderRadius: 6,
        maxBarThickness: 44,
      }],
    };
  }, [entriesData]);

  const monthlyTotal = entriesData?.monthly?.counts?.reduce((a, b) => a + b, 0) || 0;
  const weeklyTotal = entriesData?.weekly?.counts?.reduce((a, b) => a + b, 0) || 0;

  const yearOptionsForEntries = availableYears;

  if (loading) return <Loader />;

  const kpi = globalData || {};

  return (
    <div className="flex h-screen bg-gray-50">
      <AssistantSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 via-white to-white">
          <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>

              {/* Page header + global filter */}
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">Analytics</h1>
                  <p className="text-gray-600 text-sm">
                    Insights across patients, students and appointments
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-3 py-2 flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:inline">
                      Period
                    </span>
                    <ScopeFilter
                      value={globalScope}
                      years={availableYears}
                      onChange={(next) => { setGlobalScope(next); setOverrides({}); }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-gray-100 shadow-sm text-sm font-medium text-gray-600 hover:text-[#0077B6] hover:border-[#0077B6]/30 transition-colors disabled:opacity-60"
                    title="Reload analytics"
                  >
                    <FaSyncAlt className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                </div>
              </div>

              {error && (
                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {Object.keys(overrides).length > 0 && (
                <div className="mb-5 flex items-center gap-3 text-xs text-gray-500">
                  <span>Some charts use their own period.</span>
                  <button
                    type="button"
                    onClick={() => setOverrides({})}
                    className="font-semibold text-[#0077B6] hover:underline"
                  >
                    Reset all to {scopeLabel(globalScope)}
                  </button>
                </div>
              )}

              <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">

                {/* ---------------- KPIs ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaUsers}
                    title="Patient overview"
                    description={`Showing ${scopeLabel(globalScope)}`}
                  />
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard icon={FaUsers} label="Patients" value={kpi.total ?? 0}
                      hint={globalScope.scope !== 'all' ? `of ${kpi.allTimeTotal ?? 0} all time` : null} />
                    <StatCard icon={FaUserCheck} label="Assigned" value={kpi.assigned ?? 0}
                      hint={kpi.total ? `${formatShare(kpi.assigned || 0, kpi.total)} of period` : null} />
                    <StatCard icon={FaClipboardCheck} label="Unassigned" value={kpi.unassigned ?? 0} />
                    <StatCard icon={FaCalendarAlt} label="New this month" value={kpi.newThisMonth ?? 0} />
                    <StatCard icon={FaBirthdayCake} label="Average age" value={kpi.averageAge ?? 0} hint="years" />
                    <StatCard
                      icon={FaCalendarDay}
                      label="Entered today"
                      value={entriesLoading ? '...' : (entriesData?.today?.count ?? 0)}
                      hint={entriesData?.today?.date ? formatDateEG(entriesData.today.date, { weekday: undefined }) : null}
                      accent
                    />
                  </div>
                </section>

                {/* ---------------- Registrations ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaChartArea}
                    title="Registrations"
                    description="How patient intake moves over time"
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <ChartCard
                      icon={FaChartArea}
                      title="Registration trend"
                      subtitle={trendAxisLabel ? `By ${trendAxisLabel.toLowerCase()}` : null}
                      filter={filterFor('trend')}
                      height="h-72"
                      span="lg:col-span-2"
                    >
                      {isLoadingFor('trend') ? <ChartLoading />
                        : trendChart ? <Line data={trendChart} options={axisOptions()} />
                        : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaChartPie}
                      title="Assignment status"
                      subtitle="Assigned vs waiting for a student"
                      filter={filterFor('assignment')}
                      height="h-72"
                    >
                      {isLoadingFor('assignment') ? <ChartLoading />
                        : assignmentChart ? <Doughnut data={assignmentChart} options={doughnutOptions} />
                        : <EmptyChart />}
                    </ChartCard>
                  </div>
                </section>

                {/* ---------------- Demographics ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaVenusMars}
                    title="Demographics"
                    description="Each chart can be filtered on its own period"
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <ChartCard
                      icon={FaVenusMars}
                      title="Gender distribution"
                      subtitle={scopeLabel(scopeFor('gender'))}
                      filter={filterFor('gender')}
                      height="h-72"
                    >
                      {isLoadingFor('gender') ? <ChartLoading />
                        : genderChart ? <Doughnut data={genderChart} options={doughnutOptions} />
                        : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaChartBar}
                      title="Age distribution"
                      subtitle={scopeLabel(scopeFor('age'))}
                      filter={filterFor('age')}
                      height="h-72"
                    >
                      {isLoadingFor('age') ? <ChartLoading />
                        : ageChart ? <Bar data={ageChart} options={axisOptions()} />
                        : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaBirthdayCake}
                      title="Age by gender"
                      subtitle="Stacked per age band"
                      filter={filterFor('ageGender')}
                      height="h-72"
                    >
                      {isLoadingFor('ageGender') ? <ChartLoading />
                        : ageGenderChart
                          ? <Bar
                              data={ageGenderChart}
                              options={{ ...axisOptions({ stacked: true }), plugins: { ...baseOptions.plugins, legend: stackedLegend } }}
                            />
                          : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaCalendarWeek}
                      title="Registrations by weekday"
                      subtitle="Which days the clinic is busiest"
                      filter={filterFor('weekday')}
                      height="h-72"
                    >
                      {isLoadingFor('weekday') ? <ChartLoading />
                        : weekdayChart ? <Bar data={weekdayChart} options={axisOptions()} />
                        : <EmptyChart />}
                    </ChartCard>
                  </div>
                </section>

                {/* ---------------- Clinical ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaHeartbeat}
                    title="Clinical & record quality"
                    description="Recorded conditions and how complete the records are"
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <ChartCard
                      icon={FaHeartbeat}
                      title="Most recorded chronic conditions"
                      subtitle="Top 8"
                      filter={filterFor('conditions')}
                      height="h-80"
                    >
                      {isLoadingFor('conditions') ? <ChartLoading />
                        : conditionsChart ? <Bar data={conditionsChart} options={horizontalBarOptions} />
                        : <EmptyChart message="No chronic conditions recorded" />}
                    </ChartCard>

                    <ChartCard
                      icon={FaClipboardCheck}
                      title="Record completeness"
                      subtitle="Filled in vs missing"
                      filter={filterFor('completeness')}
                      height="h-80"
                    >
                      {isLoadingFor('completeness') ? <ChartLoading />
                        : completenessChart
                          ? <Bar
                              data={completenessChart}
                              options={{ ...axisOptions({ stacked: true }), plugins: { ...baseOptions.plugins, legend: stackedLegend } }}
                            />
                          : <EmptyChart />}
                    </ChartCard>
                  </div>
                </section>

                {/* ---------------- Daily / weekly entries ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaCalendarAlt}
                    title="Patient entries"
                    description="Day-by-day intake for a chosen month"
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <ChartCard
                      icon={FaCalendarAlt}
                      title="Daily patient entries"
                      subtitle={`${MONTH_NAMES[entriesMonth]} ${entriesYear} · ${monthlyTotal} total`}
                      span="lg:col-span-2"
                      height="h-72"
                      filter={
                        <div className="flex items-center gap-2">
                          <select
                            value={entriesMonth}
                            onChange={(e) => setEntriesMonth(parseInt(e.target.value, 10))}
                            className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0077B6]/25 focus:border-[#0077B6] cursor-pointer"
                            aria-label="Entries month"
                          >
                            {MONTH_NAMES.map((name, idx) => (
                              <option key={idx} value={idx}>{name}</option>
                            ))}
                          </select>
                          <select
                            value={entriesYear}
                            onChange={(e) => setEntriesYear(parseInt(e.target.value, 10))}
                            className="px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0077B6]/25 focus:border-[#0077B6] cursor-pointer"
                            aria-label="Entries year"
                          >
                            {yearOptionsForEntries.map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                      }
                    >
                      {entriesLoading ? <ChartLoading />
                        : monthlyDailyChart ? <Bar data={monthlyDailyChart} options={monthlyDailyOptions} />
                        : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaCalendarWeek}
                      title="This week"
                      subtitle={entriesData?.weekly
                        ? `${formatDateEG(entriesData.weekly.weekStart, { weekday: undefined, year: undefined })} – ${formatDateEG(entriesData.weekly.weekEnd, { weekday: undefined })} · ${weeklyTotal} total`
                        : null}
                      height="h-72"
                    >
                      {entriesLoading ? <ChartLoading />
                        : weeklyChart ? <Bar data={weeklyChart} options={axisOptions()} />
                        : <EmptyChart />}
                    </ChartCard>
                  </div>
                </section>

                {/* ---------------- Appointments ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaStethoscope}
                    title="Appointments"
                    description="Booking volume, outcome and busiest hours"
                    right={
                      <ScopeFilter
                        value={apptScope}
                        years={availableYears}
                        onChange={setApptScope}
                      />
                    }
                  />
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <ChartCard
                      icon={FaChartPie}
                      title="By status"
                      subtitle={apptAnalytics ? `${apptAnalytics.total} appointments` : null}
                      height="h-72"
                    >
                      {apptLoading ? <ChartLoading />
                        : apptStatusChart ? <Doughnut data={apptStatusChart} options={doughnutOptions} />
                        : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaClock}
                      title="Busiest hours"
                      subtitle="Appointments by start time"
                      height="h-72"
                    >
                      {apptLoading ? <ChartLoading />
                        : apptHourChart ? <Bar data={apptHourChart} options={axisOptions()} />
                        : <EmptyChart />}
                    </ChartCard>

                    <ChartCard
                      icon={FaChartBar}
                      title="By type"
                      subtitle="Top 8 appointment types"
                      height="h-72"
                    >
                      {apptLoading ? <ChartLoading />
                        : apptTypeChart ? <Bar data={apptTypeChart} options={horizontalBarOptions} />
                        : <EmptyChart />}
                    </ChartCard>
                  </div>
                </section>

                {/* ---------------- Students ---------------- */}
                <section>
                  <SectionHeader
                    icon={FaUserGraduate}
                    title="Student performance"
                    description={`${kpi.activeStudents ?? 0} students currently have patients assigned`}
                  />
                  <ChartCard
                    icon={FaUserGraduate}
                    title="Patient assignment by student"
                    subtitle="Top 10 by patient count"
                    filter={filterFor('students')}
                    height="h-96"
                  >
                    {isLoadingFor('students') ? <ChartLoading />
                      : studentsChart ? <Bar data={studentsChart} options={horizontalBarOptions} />
                      : <EmptyChart message="No patients assigned in this period" />}
                  </ChartCard>
                </section>

              </motion.div>
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default withRoleGuard(['assistant'])(Analytics);
